import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    number: "01",
    title: "Automation",
    description:
      "I build workflow automation that eliminates repetitive tasks and reclaims hours. From content pipelines to client onboarding systems - I turn manual, time-consuming processes into fast, repeatable workflows that scale without extra headcount.",
    tools: [
      { index: "01", label: "n8n, Python, Selenium" },
      { index: "02", label: "Content & caption pipelines" },
      { index: "03", label: "Onboarding & strategy workflows" },
    ],
    tag: "Automation",
  },
  {
    number: "02",
    title: "Full-Stack Development",
    description:
      "From pixel-perfect frontends to production-ready backends, I build complete web applications. I've shipped full-stack tools used daily by real teams - built to be fast, stable, and easy to maintain.",
    tools: [
      { index: "01", label: "React, Node.js, Express.js" },
      { index: "02", label: "REST APIs, Firebase, Docker" },
      { index: "03", label: "Git, GitHub, Postman" },
    ],
    tag: "Engineering",
  },
  {
    number: "03",
    title: "Mobile Development",
    description:
      "Cross-platform mobile apps with a native feel. I've built Flutter apps with role-based dashboards, OCR, blockchain payments, and real-time sync - handling both the UI and the backend in one go.",
    tools: [
      { index: "01", label: "Flutter, React Native, Expo" },
      { index: "02", label: "Firebase, Supabase, AsyncStorage" },
      { index: "03", label: "App Store & Play Store deploy" },
    ],
    tag: "Mobile",
  },
  {
    number: "04",
    title: "Web3 / Blockchain",
    description:
      "I integrate blockchain where it actually makes sense - not for the hype, but for the use case. Smart contracts, decentralized payments, and Web3-enabled frontends built for real users.",
    tools: [
      { index: "01", label: "Solidity, Hardhat, Ethers.js" },
      { index: "02", label: "MetaMask, WalletConnect" },
      { index: "03", label: "IPFS, The Graph, OpenZeppelin" },
    ],
    tag: "Web3",
  },
  {
    number: "05",
    title: "Optimization",
    description:
      "Speed, reliability, and clean architecture aren't afterthoughts - they're built in from the start. I apply CS fundamentals to keep systems fast under load, easy to scale, and straightforward to debug.",
    tools: [
      { index: "01", label: "Data Structures & Algorithms" },
      { index: "02", label: "DBMS, OOP, OS Fundamentals" },
      { index: "03", label: "Scalable systems & data pipelines" },
    ],
    tag: "Performance",
  },
];

export default function Skills() {
  const wrapperRef = useRef(null);
  const introRef = useRef(null);
  const pinnedRef = useRef(null);
  const panelsRef = useRef([]);
  const dotsRef = useRef([]);
  const bigNumDigitRef = useRef(null);
  const scrollHintRef = useRef(null);
  const headingRef = useRef(null);
  const introTagRef = useRef(null);
  const introDescRef = useRef(null);
  const introDivRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReduced) {
        gsap.globalTimeline.timeScale(1000);
      }
      
      const totalPanels = services.length;

      const getPanelChildren = (panel) =>
        [
          panel.querySelector(".srv-tag"),
          panel.querySelector(".srv-num"),
          panel.querySelector(".srv-title"),
          panel.querySelector(".srv-desc"),
          ...panel.querySelectorAll(".srv-tool"),
        ].filter(Boolean);

      gsap.fromTo(
        headingRef.current,
        { opacity: 0, y: 60, skewY: 3 },
        {
          opacity: 1,
          y: 0,
          skewY: 0,
          duration: 1.1,
          ease: "power3.out",
          scrollTrigger: { trigger: introRef.current, start: "top 80%" },
        },
      );

      gsap.fromTo(
        [introTagRef.current, introDescRef.current],
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          stagger: 0.15,
          ease: "power2.out",
          scrollTrigger: { trigger: introRef.current, start: "top 70%" },
        },
      );

      gsap.fromTo(
        introDivRef.current,
        { scaleX: 0, transformOrigin: "left center" },
        {
          scaleX: 1,
          duration: 1.2,
          ease: "power3.inOut",
          scrollTrigger: { trigger: introRef.current, start: "top 60%" },
        },
      );

      gsap.fromTo(
        ".big-num-wrap",
        { opacity: 0, scale: 0.88 },
        {
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: { trigger: pinnedRef.current, start: "top 80%" },
        },
      );

      gsap.fromTo(
        ".skills-left",
        { opacity: 0, x: -48 },
        {
          opacity: 1,
          x: 0,
          duration: 1.1,
          ease: "power3.out",
          scrollTrigger: { trigger: pinnedRef.current, start: "top 80%" },
        },
      );

      gsap.to(scrollHintRef.current, {
        opacity: 0,
        y: 8,
        duration: 0.5,
        scrollTrigger: {
          trigger: pinnedRef.current,
          start: "top+=80 top",
          toggleActions: "play none none reverse",
        },
      });

      panelsRef.current.forEach((panel, i) => {
        if (!panel) return;

        gsap.set(panel, {
          opacity: i === 0 ? 1 : 0,
          zIndex: i === 0 ? 2 : 1,
          clipPath: "inset(0 0 0% 0)",
        });

        gsap.set(getPanelChildren(panel), {
          opacity: i === 0 ? 1 : 0,
          y: 0,
        });
      });

      if (bigNumDigitRef.current)
        bigNumDigitRef.current.textContent = services[0].number[1];

      dotsRef.current.forEach((dot, i) => {
        if (!dot) return;
        gsap.set(dot, {
          width: "8px",
          height: i === 0 ? "28px" : "8px",
          background:
            i === 0 ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.2)",
        });
      });

      // FIX: Use a ref for currentPanel so closures always read the latest value
      const currentPanelRef = { value: 0 };
      // FIX: Track in-flight transition to prevent stacking animations
      const isTransitioning = { value: false };

      const isTouchDevice =
        typeof window !== "undefined" &&
        (ScrollTrigger.isTouch > 0 || navigator.maxTouchPoints > 0);

      const transition = isTouchDevice
        ? {
            panelFade: 0.14,
            childOffsetY: 8,
            childStagger: 0.012,
            childFade: 0.15,
            childEase: "power2.out",
            digitOffsetY: 6,
            digitFade: 0.14,
            dotFade: 0.1,
          }
        : {
            panelFade: 0.18,
            childOffsetY: 14,
            childStagger: 0.025,
            childFade: 0.2,
            childEase: "power3.out",
            digitOffsetY: 10,
            digitFade: 0.18,
            dotFade: 0.15,
          };

      function goToPanel(index) {
        const next = Math.max(0, Math.min(index, totalPanels - 1));
        if (next === currentPanelRef.value) return;

        const prevEl = panelsRef.current[currentPanelRef.value];
        const nextEl = panelsRef.current[next];
        if (!prevEl || !nextEl) return;

        const nextKids = getPanelChildren(nextEl);

        // FIX: Kill ALL panel tweens before switching, not just prev/next
        panelsRef.current.forEach((panel) => {
          if (!panel) return;
          gsap.killTweensOf(panel);
          gsap.killTweensOf(getPanelChildren(panel));
        });
        dotsRef.current.forEach((d) => d && gsap.killTweensOf(d));
        if (bigNumDigitRef.current) gsap.killTweensOf(bigNumDigitRef.current);

        // FIX: Immediately hide ALL panels except the incoming one
        panelsRef.current.forEach((panel, i) => {
          if (!panel || i === next) return;
          gsap.set(panel, { opacity: 0, zIndex: 1 });
          gsap.set(getPanelChildren(panel), { opacity: 0, y: 0 });
        });

        currentPanelRef.value = next;
        isTransitioning.value = true;

        gsap.set(nextEl, {
          opacity: 0,
          zIndex: 2,
          clipPath: "inset(0 0 0% 0)",
        });
        gsap.set(nextKids, { opacity: 0, y: transition.childOffsetY });

        const tl = gsap.timeline({
          onComplete: () => {
            isTransitioning.value = false;
          },
        });

        tl.to(nextEl, {
          opacity: 1,
          duration: transition.panelFade,
          ease: "power2.out",
        });

        tl.to(
          nextKids,
          {
            opacity: 1,
            y: 0,
            stagger: transition.childStagger,
            duration: transition.childFade,
            ease: transition.childEase,
          },
          "<",
        );

        if (bigNumDigitRef.current) {
          const digit = bigNumDigitRef.current;
          gsap.killTweensOf(digit);
          digit.textContent = services[next].number[1];
          gsap.fromTo(
            digit,
            { opacity: 0, y: transition.digitOffsetY },
            {
              opacity: 1,
              y: 0,
              duration: transition.digitFade,
              ease: "power3.out",
            },
          );
        }

        dotsRef.current.forEach((dot, j) => {
          if (!dot) return;
          gsap.to(dot, {
            height: j === next ? "28px" : "8px",
            background:
              j === next ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.2)",
            duration: transition.dotFade,
            ease: "power2.out",
            overwrite: true,
          });
        });
      }

      // FIX: Derive panel index purely from scroll progress — no delta/hysteresis logic
      // that could desync on rapid swipes. Round directly to nearest step.
      const getPanelIndexFromProgress = (progress) => {
        if (totalPanels <= 1) return 0;
        const raw = progress * (totalPanels - 1);
        return Math.max(0, Math.min(totalPanels - 1, Math.round(raw)));
      };

      ScrollTrigger.create({
        trigger: pinnedRef.current,
        start: "top top",
        end: () => `+=${window.innerHeight * Math.max(totalPanels - 1, 1)}`,
        pin: true,
        // FIX: Disable anticipatePin on mobile — it can misreport progress mid-swipe
        anticipatePin: 0,
        invalidateOnRefresh: true,
        fastScrollEnd: true,
        snap: prefersReduced ? false : (isTouchDevice
          ? {
              snapTo: totalPanels > 1 ? 1 / (totalPanels - 1) : 1,
              // FIX: Shorter snap duration so snap completes before next swipe
              duration: { min: 0.12, max: 0.22 },
              delay: 0,
              ease: "power1.out",
              // FIX: inertia:false prevents momentum from overshooting to wrong panel
              inertia: false,
              directional: true,
            }
          : false),
        onUpdate: (self) => {
          // FIX: Always compute panel from progress — never from a running delta
          const targetPanel = getPanelIndexFromProgress(self.progress);
          goToPanel(targetPanel);
        },
        onRefresh: (self) => {
          const targetPanel = getPanelIndexFromProgress(self.progress);
          goToPanel(targetPanel);
        },
      });
    }, wrapperRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="Skills"
      ref={wrapperRef}
      className="relative z-10 bg-[#171717] text-white rounded-t-[40px] shadow-2xl overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none z-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px",
        }}
      />

      <div
        id="Services"
        ref={introRef}
        className="relative z-10 min-h-screen flex flex-col justify-between px-8 md:px-14 pt-24 pb-16"
      >
        <div className="flex-1 flex items-center">
          <h1
            ref={headingRef}
            className="font-sat font-bold uppercase leading-none tracking-tight"
            style={{
              fontSize: "clamp(4rem, 13vw, 13rem)",
              letterSpacing: "-0.04em",
              color: "#f0ede8",
              opacity: 0,
            }}
          >
            WHAT I DO /
          </h1>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-end gap-8 md:gap-0">
          <div
            ref={introTagRef}
            className="md:w-[42%] flex items-end pb-1"
            style={{ opacity: 0 }}
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/60 border border-white/10 px-3 py-1 rounded-full">
              Services
            </span>
          </div>
          <p
            ref={introDescRef}
            className="md:w-[58%] font-sat leading-relaxed"
            style={{
              fontSize: "clamp(0.9rem, 1.15vw, 1.05rem)",
              color: "rgba(255,255,255,0.7)",
              lineHeight: 1.8,
              opacity: 0,
            }}
          >
            I build things that work - and keep working. From workflow
            automation that saves teams hours every week, to full-stack apps,
            mobile products, and Web3 integrations. I handle the full build so
            you can focus on what actually matters.
          </p>
        </div>

        <div
          ref={introDivRef}
          className="absolute bottom-0 left-0 w-full h-px bg-white/[0.08]"
          style={{ transform: "scaleX(0)", transformOrigin: "left center" }}
        />
      </div>

      <div
        ref={pinnedRef}
        className="relative z-10 w-full h-[100svh] md:h-screen overflow-hidden"
      >
        <div className="absolute top-6 left-8 z-40 flex items-center gap-3">
          <span className="text-[14px] uppercase tracking-[0.35em] text-white/60 font-medium">
            Services
          </span>
        </div>

        <div className="w-full h-full flex">
          <div className="skills-left hidden md:flex flex-col justify-center items-center w-[12%] border-r border-white/[0.05] gap-3 opacity-0">
            {services.map((_, i) => (
              <div
                key={i}
                ref={(el) => (dotsRef.current[i] = el)}
                style={{
                  width: "8px",
                  height: i === 0 ? "28px" : "8px",
                  borderRadius: "999px",
                  background:
                    i === 0 ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.2)",
                  flexShrink: 0,
                }}
              />
            ))}
          </div>

          <div className="flex-1 relative flex items-center">
            {services.map((service, i) => (
              <div
                key={i}
                ref={(el) => (panelsRef.current[i] = el)}
                className="absolute inset-0 flex flex-col justify-center px-8 md:px-14"
                style={{
                  opacity: i === 0 ? 1 : 0,
                  clipPath: "inset(0 0 0% 0)",
                }}
              >
                <div
                  className="srv-tag mb-5 self-start"
                  style={{ opacity: i === 0 ? 1 : 0 }}
                >
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 border border-white/10 px-3 py-1 rounded-full">
                    {service.tag}
                  </span>
                </div>
                <div
                  className="srv-num md:hidden text-white/40 font-bold text-xl mb-1"
                  style={{ opacity: i === 0 ? 1 : 0 }}
                >
                  ({service.number})
                </div>
                <h2
                  className="srv-title font-bold leading-none tracking-tight mb-5 font-sat"
                  style={{
                    fontSize: "clamp(2rem, 5vw, 5rem)",
                    letterSpacing: "-0.03em",
                    color: "#f0ede8",
                    opacity: i === 0 ? 1 : 0,
                  }}
                >
                  {service.title}
                </h2>
                <div className="w-full h-px bg-white/[0.08] mb-5" />
                <p
                  className="srv-desc max-w-lg mb-8 font-sat"
                  style={{
                    fontSize: "clamp(0.85rem, 1.1vw, 1rem)",
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.8,
                    opacity: i === 0 ? 1 : 0,
                  }}
                >
                  {service.description}
                </p>
                <div className="flex flex-col">
                  {service.tools.map((tool, j) => (
                    <div
                      key={j}
                      className="srv-tool flex items-center gap-5 py-4 border-t border-white/[0.06] group cursor-default"
                      style={{ opacity: i === 0 ? 1 : 0 }}
                    >
                      <span
                        className="font-mono text-[11px] tabular-nums text-white/40"
                        style={{ minWidth: "1.8rem" }}
                      >
                        {tool.index}
                      </span>
                      <span
                        className="font-bold font-sat group-hover:text-white/95 transition-colors duration-300"
                        style={{
                          fontSize: "clamp(0.95rem, 1.5vw, 1.35rem)",
                          color: "rgba(255,255,255,0.72)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {tool.label}
                      </span>
                      <span className="ml-auto text-white/0 group-hover:text-white/25 transition-all duration-300 text-sm">
                        -&gt;
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-white/[0.06]" />
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:flex w-[30%] items-center justify-center border-l border-white/[0.04] overflow-hidden relative">
            <div
              className="big-num-wrap select-none pointer-events-none font-bold leading-none flex items-center"
              style={{
                fontSize: "clamp(7rem, 14vw, 16rem)",
                letterSpacing: "-0.06em",
                color: "rgba(255,255,255,1)",
                opacity: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <span>(0</span>
              <span
                ref={bigNumDigitRef}
                className="inline-block"
                style={{ minWidth: "0.72em", textAlign: "center" }}
              >
                1
              </span>
              <span>)</span>
            </div>
          </div>
        </div>

        <div
          ref={scrollHintRef}
          className="absolute bottom-7 right-8 z-40 flex items-center gap-2 text-white/50"
          style={{
            fontSize: "11px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
          }}
        >
          <span>Scroll</span>
          <span className="w-5 h-px bg-white/40" />
        </div>
      </div>
    </section>
  );
}